<template>
  <ak-modal v-model:active="isModalActive" size="big">
    <ak-modal-body>
      <ak-modal-title>Generated List</ak-modal-title>
      <ak-box
        rounded
        background="grey-lightest"
        class="cursor-pointer"
        @click.prevent="copyToClipboard"
      >
        {{ emailString }}
        <ak-loading
          v-model:active="loading"
          :is-full-page="false"
          :can-cancel="false"
        />
      </ak-box>
      <ak-button
        color="primary"
        class="mt-small pull-right"
        :disabled="loading"
        @click.prevent="copyToClipboard"
      >
        Copy
        <ak-icon type="copy" size="big" class="ml-tiny" />
      </ak-button>
    </ak-modal-body>
    <ak-modal-footer>
      <ak-modal-button
        :disabled="loading"
        @click.prevent="isModalActive = false"
      >
        Cancel
      </ak-modal-button>
      <ak-modal-button
        :disabled="loading"
        color="success"
        @click.prevent="processTransactions"
      >
        Process
      </ak-modal-button>
    </ak-modal-footer>
  </ak-modal>
</template>

<script>
import { computed, ref, watch, defineComponent } from 'vue'
import ModalButton from '@akerolabs/akero-vue/src/components/modal/ModalButton.vue'
import Modal from '@akerolabs/akero-vue/src/components/modal/Modal.vue'
import ModalBody from '@akerolabs/akero-vue/src/components/modal/ModalBody.vue'
import ModalFooter from '@akerolabs/akero-vue/src/components/modal/ModalFooter.vue'
import ModalTitle from '@akerolabs/akero-vue/src/components/modal/ModalTitle.vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import Loading from '@akerolabs/akero-vue/src/components/loading/Loading.vue'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src/components/toast'

export default defineComponent({
  name: 'AkGeneratedModal',
  components: {
    [Modal.name]: Modal,
    [ModalTitle.name]: ModalTitle,
    [ModalButton.name]: ModalButton,
    [Box.name]: Box,
    [Button.name]: Button,
    [Icon.name]: Icon,
    [Loading.name]: Loading,
    [ModalBody.name]: ModalBody,
    [ModalFooter.name]: ModalFooter,
  },
  props: {
    /**
     * @type {Boolean}
     * @description use to display and hide the modal
     */
    isActive: {
      type: Boolean,
      default: false,
    },
    /**
     * @type {Object}
     * @description The current user object
     */
    user: {
      type: Object,
      required: true,
    },

    /**
     * @type {String}
     * @description The current account _id
     */
    account: {
      required: true,
      type: String,
    },

    /**
     * @type
     */
    agency: {
      required: true,
      type: String,
    },
  },
  emits: ['update:isActive', 'has-processed'],
  setup(props, { emit }) {
    const generated = ref([])
    const loading = ref(true)
    const isModalActive = ref(false)

    /**
     * @description Emits the update to the isActive prop and resets the generated list when modal is closed
     */
    watch(isModalActive, async (value) => {
      emit('update:isActive', value)
      if (!value) {
        generated.value = []
      } else {
        await fetchGenerated()
      }
    })

    /**
     * @description Updates the internal isModalActive based on the isActive prop
     */
    watch(
      () => props.isActive,
      (value) => {
        isModalActive.value = value
      },
    )

    /**
     * @description Returns a comma separated list of email addresses as a string
     * @return {String}
     */
    const emailString = computed(() => {
      const emails = []
      generated.value.forEach((i) => {
        emails.push(i.contact_email)
      })
      return emails.join(', ')
    })

    /**
     * @description Returns an object of the params needed for a fetch request
     * @return {Object}
     */
    const generatedRequest = computed(() => {
      return {
        params: {
          account: props.account,
          agency: props.agency,
          type: 'withdraw',
          reason: 'contact_request',
          deleted: false,
          processed: { $ne: true },
          approved: true,
          generated: true,
          rejected: { $ne: true },
          per_page: 1000000,
          page: 0,
        },
      }
    })

    /**
     * @description Makes and api request to get all transactions in the generated state
     */
    const fetchGenerated = async () => {
      loading.value = true

      let generatedData = []
      const displayError = (ex) => {
        if (ex) console.log(ex)
        ToastProgrammatic.open({
          message: `Error failed to fetch generated`,
          type: 'danger',
        })
      }
      try {
        const { data } = await request.get(
          '/withdrawal/transactions',
          generatedRequest.value,
        )
        generatedData = data.data
      } catch (ex) {
        generatedData = []
        displayError(ex)
      }
      generated.value = generatedData
      loading.value = false
    }

    /**
     * @description Copies the comma separated email to the clipboard
     */
    const copyToClipboard = () => {
      navigator.clipboard.writeText(emailString.value)
      ToastProgrammatic.open({
        message: 'Copied to clipboard',
        type: 'success',
      })
    }

    /**
     * @description An array of the generated transaction Ids
     * @return {Array<String>}
     */
    const transactionIds = computed(() => {
      const ids = []
      generated.value.forEach((i) => {
        ids.push(i._id)
      })
      return ids
    })

    /**
     * @description Makes an api request to move generated transactions to processed
     */
    const processTransactions = async () => {
      loading.value = true
      const displayError = (ex) => {
        if (ex) console.log(ex)
        ToastProgrammatic.open({
          message: `Error failed to process generated`,
          type: 'danger',
        })
      }
      const displaySuccess = () => {
        ToastProgrammatic.open({
          message: 'Success processed generated',
          type: 'success',
        })
      }
      try {
        await request.post('/withdrawal/transactions/process', {
          ids: transactionIds.value,
          processedBy: props.user._id,
          processedByName: props.user.name,
        })
        loading.value = false
        isModalActive.value = false
        displaySuccess()
        emit('has-processed')
      } catch (ex) {
        loading.value = false
        displayError(ex)
      }
    }

    return {
      isModalActive,
      generated,
      loading,
      emailString,
      copyToClipboard,
      processTransactions,
    }
  },
})
</script>
